import { useState } from 'react';
import { Aviso, Botao, Campo, CampoDeSelecao, Cartao } from '../../componentes/ui';
import {
  DEFAULTS_DE_FABRICA,
  gravarDefaults,
  lerDefaults,
  limparDefaults,
  type DefaultsDaSimulacao,
} from '../passivo/configuracoes';
import type { AbaDeConfiguracao } from './BarraDeAbas';

/**
 * As configurações do Créditos ICMS Passivos, na Central de Configurações.
 *
 * A aba "Simulação" define o ponto de partida da tela de simulação: deságio,
 * parcelas, juros e sistema. É preferência deste navegador, não da conta — o
 * porquê está em `passivo/configuracoes.ts`.
 */
export function abasDoPassivo(): AbaDeConfiguracao[] {
  return [{ id: 'simulacao', rotulo: 'Simulação', conteudo: <DefaultsDaSimulacaoForm /> }];
}

/* ─── Aba Simulação ───────────────────────────────────────────────────────── */

function DefaultsDaSimulacaoForm(): JSX.Element {
  const [form, setForm] = useState<DefaultsDaSimulacao>(() => lerDefaults());
  const [situacao, setSituacao] = useState<'nada' | 'salvo' | 'recusado' | 'restaurado'>('nada');

  const definir = (campo: keyof DefaultsDaSimulacao, valor: string): void => {
    setSituacao('nada');
    setForm((f) => ({ ...f, [campo]: valor }));
  };

  const problemas = validar(form);
  const gravado = lerDefaults();
  const mudou = (Object.keys(form) as (keyof DefaultsDaSimulacao)[]).some(
    (k) => form[k] !== gravado[k],
  );

  return (
    <div className="grid gap-4 lg:grid-cols-[28rem_1fr]">
      <Cartao como="section" titulo="Valores iniciais da simulação">
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (problemas.length > 0) return;
            // A leitura normaliza (vírgula → ponto, faixa); gravar o que ela
            // devolve é o que garante que o formulário e o gravado coincidem.
            const ok = gravarDefaults(form);
            setSituacao(ok ? 'salvo' : 'recusado');
            if (ok) setForm(lerDefaults());
          }}
        >
          <Campo
            rotulo="Deságio"
            numerico
            sufixo="%"
            ajuda="Percentual de desconto sobre o crédito comprado, entre 0 e 100."
            value={form.percentualDesagio}
            onChange={(e) => definir('percentualDesagio', e.target.value)}
          />
          <Campo
            rotulo="Parcelas"
            numerico
            ajuda="Número inteiro, entre 1 e 360."
            value={form.quantidadeParcelas}
            onChange={(e) => definir('quantidadeParcelas', e.target.value)}
          />
          <Campo
            rotulo="Taxa de juros mensal"
            numerico
            sufixo="%"
            ajuda="Entre 0 e 10."
            value={form.taxaJurosMensal}
            onChange={(e) => definir('taxaJurosMensal', e.target.value)}
          />
          <CampoDeSelecao
            rotulo="Sistema de amortização"
            ajuda="SAC tem parcela decrescente; Price, parcela fixa."
            opcoes={[
              { valor: 'sac', rotulo: 'SAC' },
              { valor: 'price', rotulo: 'Price' },
            ]}
            value={form.indice}
            onChange={(e) => definir('indice', e.target.value)}
          />

          {problemas.length > 0 && (
            <Aviso tom="atencao" titulo="Confira os valores">
              <ul className="mt-1 flex list-disc flex-col gap-1 pl-4">
                {problemas.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            </Aviso>
          )}

          {situacao === 'salvo' && (
            <Aviso tom="positivo" titulo="Valores salvos">
              A próxima simulação abre com estes valores.
            </Aviso>
          )}
          {situacao === 'restaurado' && (
            <Aviso tom="informacao" titulo="Padrão restaurado">
              A simulação volta a abrir com os valores de fábrica.
            </Aviso>
          )}
          {situacao === 'recusado' && (
            <Aviso tom="atencao" titulo="O navegador não deixou gravar">
              Sem armazenamento local (janela privada ou dados de site bloqueados) a preferência não
              persiste. A simulação continua funcionando com os valores de fábrica.
            </Aviso>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <Botao type="submit" disabled={!mudou || problemas.length > 0}>
              Salvar
            </Botao>
            <Botao
              variante="sutil"
              onClick={() => {
                limparDefaults();
                setForm(DEFAULTS_DE_FABRICA);
                setSituacao('restaurado');
              }}
            >
              Restaurar padrão
            </Botao>
          </div>
        </form>
      </Cartao>

      <Aviso tom="informacao" titulo="Preferência deste navegador">
        Estes valores ficam guardados neste navegador, não na sua conta: em outra máquina, ou depois
        de limpar os dados do site, a simulação volta ao padrão de fábrica (
        {DEFAULTS_DE_FABRICA.percentualDesagio}% de deságio,{' '}
        {DEFAULTS_DE_FABRICA.quantidadeParcelas} parcelas, {DEFAULTS_DE_FABRICA.taxaJurosMensal}% ao
        mês, SAC). Eles só definem o ponto de partida — cada contrato guarda os seus próprios
        parâmetros.
      </Aviso>
    </div>
  );
}

/** As mesmas faixas que `lerDefaults` aceita — o que passa aqui é o que se lê de volta. */
function validar(f: DefaultsDaSimulacao): string[] {
  const problemas: string[] = [];
  const desagio = numero(f.percentualDesagio);
  const parcelas = numero(f.quantidadeParcelas);
  const juros = numero(f.taxaJurosMensal);

  if (desagio === null || desagio < 0 || desagio > 100) {
    problemas.push('Deságio precisa ser um percentual entre 0 e 100.');
  }
  if (parcelas === null || !Number.isInteger(parcelas) || parcelas < 1 || parcelas > 360) {
    problemas.push('Parcelas precisa ser um número inteiro entre 1 e 360.');
  }
  if (juros === null || juros < 0 || juros > 10) {
    problemas.push('Taxa de juros mensal precisa estar entre 0 e 10.');
  }
  return problemas;
}

function numero(texto: string): number | null {
  if (texto.trim() === '') return null;
  const n = Number(texto.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}
